import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useResearchStore } from '@/stores/researchStore';
import { Header } from '@/components/Header';
import { SearchFilters } from '@/components/research/SearchFilters';
import { LeadsTable } from '@/components/research/LeadsTable';
import {
  LinkedInScrapeModal,
  LinkedInResultsModal,
  DataFieldsInfoModal,
  ScheduleModal,
  SaveSearchModal,
  SavedSearchesDrawer,
} from '@/components/research/ResearchModals';
import { searchLeads, researchContact, exportLeadsToCSV, listSavedSearches, updateSavedSearchPage } from '@/services/research';
import { deductCredits, getCreditBalances } from '@/services/credits';
import { loadAllContacts } from '@/services/contacts';
import { FEATURE_FLAGS } from '@/services/api';
import type { SearchFilters as SearchFiltersType } from '@/types/research';

const RESEARCH_CREDIT_COST = 1;
const DEFAULT_TITLES = ['CEO', 'Chief Executive Officer', 'Owner', 'Founder', 'Co-Founder'];

export function CompanySearch() {
  const { userEmail, isAuthenticated, addScrapeJob, updateScrapeJob } = useAppStore();
  const [dataFieldsModalOpen, setDataFieldsModalOpen] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companyMatchSet, setCompanyMatchSet] = useState<Set<string>>(new Set());
  const [csvStatus, setCsvStatus] = useState<string | null>(null);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [contactCredits, setContactCredits] = useState(0);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const csvRunLockRef = useRef(false);

  const {
    filters,
    leads,
    totalResults,
    currentPage,
    perPage,
    loading,
    researchedContacts,
    pendingSearch,
    activeSavedSearchId,
    setLeads,
    setCurrentPage,
    setLoading,
    addRecentSearch,
    setSavedSearches,
    updateSavedSearchPage: updateSavedSearchPageLocal,
    getSelectedIndices,
    saveResearchedContact,
    setPendingSearch,
    setActiveSavedSearchId,
    setFilters,
  } = useResearchStore();

  // Load credits + saved searches
  useEffect(() => {
    if (userEmail) {
      getCreditBalances(userEmail)
        .then((res) => setContactCredits(res.contactCredits))
        .catch(console.error);
      listSavedSearches(userEmail)
        .then((res) => {
          if (res.success) setSavedSearches(res.savedSearches);
        })
        .catch(console.error);
    }
  }, [userEmail, setSavedSearches]);

  // Default titles for company search
  useEffect(() => {
    if (filters.titles.length === 0) {
      setFilters({ titles: DEFAULT_TITLES });
    }
  }, [filters.titles.length, setFilters]);

  // Load companies (from contacts) for dedupe
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    const controller = new AbortController();
    const normalize = (val?: string) => (val || '').toString().trim().toLowerCase();

    const run = async () => {
      setCompaniesLoading(true);
      try {
        const contacts = await loadAllContacts(userEmail, { signal: controller.signal });
        if (controller.signal.aborted || cancelled) return;
        const set = new Set<string>();
        for (const c of contacts || []) {
          const company = normalize((c.company as string) || (c.Company as string));
          const industry = normalize(((c as any).CompanyIndustry as string) || ((c as any).Industry as string));
          if (!company) continue;
          set.add(`${company}::${industry}`);
        }
        if (!cancelled) setCompanyMatchSet(set);
      } catch (e) {
        if ((e as Error)?.name !== 'AbortError') {
          console.error('Failed to load companies for match:', e);
        }
      } finally {
        if (!cancelled) setCompaniesLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userEmail]);

  const isCompanyMatch = useCallback((lead: any) => {
    const normalize = (val?: string) => (val || '').toString().trim().toLowerCase();
    const company =
      typeof lead.company === 'string'
        ? lead.company
        : lead.company?.name || lead.companyName || lead.Company || '';
    const industry =
      (typeof lead.company === 'string' ? '' : lead.company?.industry) || lead.industry || '';
    const key = `${normalize(company)}::${normalize(industry)}`;
    return companyMatchSet.has(key);
  }, [companyMatchSet]);

  // Search handler
  const handleSearch = useCallback(async (nextFilters?: SearchFiltersType) => {
    const activeFilters = nextFilters ?? filters;
    setLoading(true);
    addRecentSearch(activeFilters);
    setActiveSavedSearchId(null);
    setCurrentPage(0);

    try {
      const result = await searchLeads(activeFilters, 0, perPage);
      setLeads(result.contacts, result.total);
    } catch (e) {
      console.error(e);
      alert('Search failed');
    } finally {
      setLoading(false);
    }
  }, [filters, perPage, setLoading, setLeads, setCurrentPage, addRecentSearch, setActiveSavedSearchId]);

  useEffect(() => {
    if (!pendingSearch) return;
    const run = async () => {
      setLoading(true);
      try {
        const result = await searchLeads(pendingSearch.filters, pendingSearch.page, pendingSearch.perPage);
        setLeads(result.contacts, result.total);
        setCurrentPage(pendingSearch.page);
        if (activeSavedSearchId && userEmail) {
          await updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: pendingSearch.page, perPage: pendingSearch.perPage });
          updateSavedSearchPageLocal(activeSavedSearchId, pendingSearch.page, pendingSearch.perPage);
        }
      } catch (e) {
        console.error(e);
        alert('Search failed');
      } finally {
        setLoading(false);
        setPendingSearch(null);
      }
    };
    run();
  }, [pendingSearch, setLeads, setCurrentPage, setLoading, setPendingSearch, activeSavedSearchId, userEmail, updateSavedSearchPageLocal]);

  useEffect(() => {
    if (!activeSavedSearchId || !userEmail) return;
    updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: currentPage, perPage })
      .then(() => updateSavedSearchPageLocal(activeSavedSearchId, currentPage, perPage))
      .catch(console.error);
  }, [activeSavedSearchId, currentPage, perPage, userEmail, updateSavedSearchPageLocal]);

  // Pagination
  const handlePrevPage = useCallback(async () => {
    if (currentPage === 0) return;
    setLoading(true);
    try {
      const result = await searchLeads(filters, currentPage - 1, perPage);
      setLeads(result.contacts, result.total);
      setCurrentPage(currentPage - 1);
      if (activeSavedSearchId && userEmail) {
        await updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: currentPage - 1, perPage });
        updateSavedSearchPageLocal(activeSavedSearchId, currentPage - 1, perPage);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, perPage, setLeads, setCurrentPage, setLoading, activeSavedSearchId, userEmail, updateSavedSearchPageLocal]);

  const handleNextPage = useCallback(async () => {
    const totalPages = Math.ceil(totalResults / perPage);
    if (currentPage >= totalPages - 1) return;
    setLoading(true);
    try {
      const result = await searchLeads(filters, currentPage + 1, perPage);
      setLeads(result.contacts, result.total);
      setCurrentPage(currentPage + 1);
      if (activeSavedSearchId && userEmail) {
        await updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: currentPage + 1, perPage });
        updateSavedSearchPageLocal(activeSavedSearchId, currentPage + 1, perPage);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, totalResults, filters, perPage, setLeads, setCurrentPage, setLoading, activeSavedSearchId, userEmail, updateSavedSearchPageLocal]);

  const startResearchJob = useCallback((label: string, total: number) => {
    const id = `research-${label.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
    addScrapeJob({
      id,
      label,
      source: 'research',
      companyName: '',
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: `Starting 0/${total}`,
    });
    updateScrapeJob(id, { status: 'running', startedAt: new Date().toISOString() });
    return id;
  }, [addScrapeJob, updateScrapeJob]);

  const consumeResearchCredit = useCallback(async () => {
    if (FEATURE_FLAGS.SERVER_SIDE_CREDITS) return;
    if (!userEmail) throw new Error('Missing user');
    const updated = await deductCredits(userEmail, RESEARCH_CREDIT_COST, 'research_contact', 'contact');
    setContactCredits(updated);
  }, [userEmail]);

  const runResearchQueue = useCallback((indices: number[], label: string) => {
    const total = indices.length;
    const jobId = startResearchJob(label, total);
    let saved = 0;
    let done = 0;

    Promise.resolve().then(async () => {
      try {
        for (const i of indices) {
          const lead = leads[i];
          if (!lead) continue;
          const displayName =
            lead.name ||
            `${lead.firstName || ''} ${lead.lastName || ''}`.trim() ||
            lead.company ||
            lead.title ||
            'Contact';
          await consumeResearchCredit();
          const result = await researchContact(lead, userEmail);
          if (result.success && result.data) {
            saveResearchedContact(lead.searchResultId, result.data);
            saved += 1;
          }
          done += 1;
          const progress = Math.min(95, Math.max(10, Math.round((done / total) * 100)));
          updateScrapeJob(jobId, { progress, message: `Researching ${done}/${total} · ${displayName}` });
        }
        updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: `Complete (${saved}/${total})`, finishedAt: new Date().toISOString() });
        alert(`Researched ${saved} of ${total} contacts`);
      } catch (e) {
        console.error(e);
        updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed', finishedAt: new Date().toISOString() });
      }
    });
  }, [leads, consumeResearchCredit, saveResearchedContact, updateScrapeJob, startResearchJob]);

  const handleResearchSelected = () => {
    const indices = getSelectedIndices();
    if (!indices.length) return alert('Select at least one lead');
    runResearchQueue(indices, 'Selected Leads');
  };

  const handleResearchAll = () => {
    if (!leads.length) return;
    const indices = leads.map((_, idx) => idx);
    runResearchQueue(indices, 'All Leads');
  };

  const handleExport = async () => {
    try {
    const fileName = await exportLeadsToCSV(leads, researchedContacts);
      alert(`Exported ${fileName}`);
    } catch (e) {
      console.error(e);
    }
  };

  const totalPages = Math.ceil(totalResults / perPage);

  const splitCsvLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((v) => v.trim());
  };

  const normalizeKeyword = (value: string): string =>
    value
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/+$/, '');

  const normalizeText = (value: string): string =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const departmentTitleHints = (departments: string[]): string[] => {
    const map: Record<string, string[]> = {
      sales: ['Sales', 'Account Executive', 'Business Development', 'SDR', 'BDR', 'Revenue'],
      marketing: ['Marketing', 'Demand Generation', 'Growth', 'Brand', 'Content'],
      'human resources': ['HR', 'Human Resources', 'People Operations', 'Talent', 'Recruiting'],
      it: ['IT', 'Information Technology', 'Infrastructure', 'Systems', 'Security'],
      engineering: ['Engineering', 'Software Engineer', 'Developer', 'Technical Lead', 'Architect'],
      operations: ['Operations', 'COO', 'Ops', 'Process', 'Fulfillment'],
      finance: ['Finance', 'CFO', 'Controller', 'FP&A', 'Accounting'],
      support: ['Support', 'Customer Success', 'Customer Support', 'Service'],
      legal: ['Legal', 'General Counsel', 'Counsel', 'Compliance'],
      'project management': ['Project Manager', 'Program Manager', 'PMO'],
    };
    const out: string[] = [];
    for (const d of departments) {
      const key = d.toLowerCase().trim();
      const hints = map[key];
      if (hints?.length) out.push(...hints);
    }
    return Array.from(new Set(out));
  };

  const handleImportCsv = async (file: File) => {
    if (csvProcessing || csvRunLockRef.current) {
      setCsvStatus('CSV import is already running.');
      return;
    }
    setCsvStatus(null);
    if (!userEmail) {
      setCsvStatus('Missing user context.');
      return;
    }
    const text = await file.text();
    const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim().length);
    if (lines.length < 2) {
      setCsvStatus('CSV has no data rows.');
      return;
    }
    const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
    const headerIndex = (candidates: string[]) =>
      headers.findIndex((h) => candidates.includes(h));

    const companyIdx = headerIndex(['company', 'company name', 'company_name', 'companyname']);
    const industryIdx = headerIndex(['industry', 'company industry', 'company_industry']);
    const websiteIdx = headerIndex(['website', 'url', 'domain', 'company website', 'company_website', 'company url', 'company_url']);
    const keywordIdx = headerIndex(['keyword', 'keywords', 'search keyword', 'search_keyword']);

    if (companyIdx === -1 && websiteIdx === -1) {
      setCsvStatus('Missing required headers. Need at least company or website/url.');
      return;
    }

    const rows: Array<{ company: string; industry: string; website: string; keyword: string }> = [];
    const seen = new Set<string>();

    for (let i = 1; i < lines.length; i += 1) {
      const cols = splitCsvLine(lines[i]);
      const company = companyIdx >= 0 ? (cols[companyIdx] || '').trim() : '';
      const industry = industryIdx >= 0 ? (cols[industryIdx] || '').trim() : '';
      const websiteRaw = websiteIdx >= 0 ? (cols[websiteIdx] || '').trim() : '';
      const website = normalizeKeyword(websiteRaw);
      const keywordRaw = keywordIdx >= 0 ? (cols[keywordIdx] || '').trim() : '';
      const keyword = normalizeKeyword(keywordRaw);
      if (!company && !website) continue;
      const key = `${company.toLowerCase()}::${website.toLowerCase()}::${(industry || '').toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({ company, industry: industry || '', website, keyword });
    }

    if (!rows.length) {
      setCsvStatus('No valid rows with company names.');
      return;
    }

    setCurrentPage(0);
    setCsvStatus(`Imported ${rows.length} companies. Running search + research...`);
    setCsvProcessing(true);
    csvRunLockRef.current = true;

    const jobId = startResearchJob('Company CSV Search + Research', rows.length);
    updateScrapeJob(jobId, {
      status: 'running',
      progress: 8,
      message: `Starting 0/${rows.length}`,
      startedAt: new Date().toISOString(),
    });

    try {
      let processed = 0;
      let matched = 0;
      let researched = 0;
      let failed = 0;
      const collectedLeads: any[] = [];

      for (const row of rows) {
        try {
          const rowKeyword = row.website || row.keyword || row.industry || '';
          const selectedTitles = Array.isArray(filters.titles) ? filters.titles : [];
          const selectedDeptHints = departmentTitleHints(Array.isArray(filters.departments) ? filters.departments : []);
          const mergedTitles = Array.from(new Set([...selectedTitles, ...selectedDeptHints, ...DEFAULT_TITLES]));

          setFilters({
            companies: row.company ? [row.company] : [],
            industries: Array.isArray(filters.industries) ? filters.industries : [],
            titles: mergedTitles,
            keywords: rowKeyword ? [rowKeyword] : [],
          });

          const rowFilters = {
            names: [],
            companies: row.company ? [row.company] : [],
            titles: mergedTitles,
            seniorities: Array.isArray(filters.seniorities) ? filters.seniorities : [],
            departments: [],
            industries: Array.isArray(filters.industries) ? filters.industries : [],
            locations: Array.isArray(filters.locations) ? filters.locations : [],
            employeeSizes: Array.isArray(filters.employeeSizes) ? filters.employeeSizes : [],
            estimatedRevenues: Array.isArray(filters.estimatedRevenues) ? filters.estimatedRevenues : [],
            keywords: rowKeyword ? [rowKeyword] : [],
          };
          let found = await searchLeads(rowFilters, 0, 50);
          if ((!found.contacts || found.contacts.length === 0) && row.company) {
            found = await searchLeads({ ...rowFilters, keywords: [], industries: [] }, 0, 50);
          }
          const rowCompany = normalizeText(row.company);
          const rowWebsite = normalizeKeyword(row.website);
          const candidates = Array.isArray(found.contacts) ? found.contacts : [];
          const scored = candidates
            .map((lead: any) => {
              const company = normalizeText(
                (typeof lead.company === 'string' ? lead.company : lead.company?.name) ||
                  lead.companyName ||
                  lead.Company ||
                  ''
              );
              const leadDomain = normalizeKeyword(lead.domain || '');
              const title = normalizeText(lead.title || '');
              let score = 0;
              if (rowCompany && company && company === rowCompany) score += 60;
              if (rowWebsite && leadDomain && (leadDomain === rowWebsite || leadDomain.includes(rowWebsite) || rowWebsite.includes(leadDomain))) score += 25;
              if (title.includes('owner') || title.includes('founder') || title.includes('chief executive officer') || title === 'ceo' || title.includes(' co founder ') || title.startsWith('co founder')) score += 15;
              return { lead, score };
            })
            .sort((a, b) => b.score - a.score);
          const selectedLeads = scored.slice(0, 5).map((x) => x.lead);
          if (!selectedLeads.length) {
            failed += 1;
          } else {
            matched += selectedLeads.length;
            for (const lead of selectedLeads) {
              collectedLeads.push(lead);
              try {
                await consumeResearchCredit();
                const result = await researchContact(lead, userEmail);
                if (result.success && result.data) {
                  saveResearchedContact(lead.searchResultId, result.data);
                  researched += 1;
                } else {
                  failed += 1;
                }
              } catch (researchErr) {
                failed += 1;
                console.error('CSV row research failed', row, researchErr);
              }
            }
          }
        } catch (err) {
          failed += 1;
          console.error('CSV company research row failed', row, err);
        } finally {
          processed += 1;
          const progress = Math.min(95, Math.max(10, Math.round((processed / rows.length) * 100)));
          updateScrapeJob(jobId, {
            progress,
            message: `Processed ${processed}/${rows.length} · matched ${matched} · researched ${researched}`,
          });
        }
      }

      if (collectedLeads.length) {
        setLeads(collectedLeads, collectedLeads.length);
        setCurrentPage(0);
      }

      updateScrapeJob(jobId, {
        status: failed > 0 ? 'failed' : 'succeeded',
        progress: 100,
        message:
          failed > 0
            ? `Completed with errors · researched ${researched}/${rows.length}`
            : `Complete (${researched}/${rows.length})`,
        finishedAt: new Date().toISOString(),
      });
      setCsvStatus(
        failed > 0
          ? `Finished with ${failed} errors. Researched ${researched}/${rows.length}.`
          : `Finished. Researched ${researched}/${rows.length}.`
      );
    } finally {
      setCsvProcessing(false);
      csvRunLockRef.current = false;
    }
  };
  const doneCount = useMemo(() => Object.keys(researchedContacts || {}).length, [researchedContacts]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />

      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Company Search</h2>
            <p className="text-sm text-gray-400 mt-1">
              Find CEO/Owner/Founder contacts and match against your company list.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Defaults loaded: {DEFAULT_TITLES.join(', ')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDataFieldsModalOpen(true)}
              className="px-3 py-2 rounded bg-gray-800 text-gray-200 hover:text-white text-sm"
            >
              What does this include?
            </button>
          </div>
        </div>
      </div>

      {companiesLoading && (
        <div className="px-6 py-3 border-b border-gray-800 bg-gray-950">
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <div className="h-2 w-2 animate-pulse rounded-full bg-yellow-400" />
            Loading company list for match…
          </div>
        </div>
      )}

      <div className="px-6 pt-4">
        <SearchFilters onSearch={handleSearch} hideTitles hideSeniority hideDepartments />
      </div>
      <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-400">
              Results: <span className="text-white font-semibold">{totalResults}</span> · Done: <span className="text-emerald-300 font-semibold">{doneCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleResearchSelected}
                className="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded text-sm"
              >
                Get Data (Selected)
              </button>
              <button
                onClick={handleResearchAll}
                className="bg-green-700 hover:bg-green-600 text-white px-3 py-2 rounded text-sm"
              >
                Get Data (All)
              </button>
              <button
                onClick={handleExport}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Export
              </button>
              <button
                onClick={() => csvInputRef.current?.click()}
                disabled={csvProcessing}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded text-sm"
              >
                {csvProcessing ? 'Import Running…' : 'Import CSV'}
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImportCsv(file);
                  }
                  e.currentTarget.value = '';
                }}
              />
              {csvStatus && <span className="text-xs text-gray-400">{csvStatus}</span>}
            </div>
          </div>

          {loading && (
            <div className="bg-gray-900/80 border border-gray-800 rounded-lg p-6 text-center text-gray-300">
              Searching…
            </div>
          )}

          <LeadsTable
            onResearchComplete={() => {}}
            companyMatch={isCompanyMatch}
            showCrunchbaseButton
            contactCredits={contactCredits}
            setContactCredits={setContactCredits}
          />

          <div className="flex items-center justify-between mt-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="px-3 py-2 bg-gray-800 rounded text-white disabled:opacity-50 flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <div className="text-sm text-gray-400">
              Page {currentPage + 1} of {totalPages || 1}
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage >= totalPages - 1}
              className="px-3 py-2 bg-gray-800 rounded text-white disabled:opacity-50 flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
      </div>

      {dataFieldsModalOpen && (
        <DataFieldsInfoModal open={dataFieldsModalOpen} onClose={() => setDataFieldsModalOpen(false)} />
      )}
      <LinkedInScrapeModal />
      <LinkedInResultsModal />
      <ScheduleModal mode="company" />
      <SaveSearchModal />
      <SavedSearchesDrawer />
    </div>
  );
}
